import { P2GCheckBox, P2GFade, P2GGrid, P2GLink, P2GText, useP2GInView } from '@parcel2go/core-ui';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useOptIns from '~/hooks/use-opt-ins';
import useWorkflowActions from '~/hooks/use-workflow-actions';
import { TranslationNamespaces } from '~/lib/constants';
import { useTenant } from '@parcel2go/npm-p2g-app';

const GetDefaultOptInMessage = (contentKey: string | undefined, company: string) => {
  switch (contentKey) {
    case '01':
      return (
        <P2GText variant="body2">
          As part of our commitment to enhancing your experience, we can keep you updated with valuable information,
          such as exclusive offers, products, services and updates about your parcel(s) and your account. You can manage
          these preferences at any time in your{' '}
          <P2GLink href="/myaccount/preferences" target="_blank">
            My Account
          </P2GLink>{' '}
          area or clicking the unsubscribe link.
          <br />
          <br />
          Please note that when we say ‘We’, we are referring to {company}, and rest assured, we will never trade or
          disclose your information to any third party. For additional details, please refer to our{' '}
          <P2GLink href="/privacy-policy" target="_blank">
            Privacy Policy
          </P2GLink>
        </P2GText>
      );
    case '02':
      return (
        <P2GText variant="body2">
          As part of our service to you we will keep you updated with valuable information, such as exclusive offers,
          products, services and updates about your parcel(s) and your account. You can change your mind at any time by
          opting out in your ‘
          <P2GLink href="/myaccount/preferences" target="_blank">
            My Account
          </P2GLink>
          ’ area or clicking the unsubscribe link.
          <br />
          <br />
          Please note that when we say ‘We’, we are referring to {company}, and rest assured, we will never trade or
          disclose your information to any third party. For additional details, please refer to our{' '}
          <P2GLink href="/privacy-policy" target="_blank">
            Privacy Policy
          </P2GLink>
          .
        </P2GText>
      );
    case '03':
      return (
        <P2GText variant="body2">
          As part of our service to you we will contact you with updates about your parcel(s) and your account.
          <br />
          <br />
          Please note that when we say ‘We’, we are referring to {company}, and rest assured, we will never trade or
          disclose your information to any third party. For additional details, please refer to our{' '}
          <P2GLink href="/privacy-policy" target="_blank">
            Privacy Policy
          </P2GLink>
          .
        </P2GText>
      );
    default:
      return <></>;
  }
};

type props = {
  wrapper: (c: React.ReactNode) => React.ReactNode;
};

const OptIn: React.FC<props> = ({ wrapper }) => {
  const { addCompletedAction } = useWorkflowActions();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const optIn = useOptIns();
  const selectToOptOut = optIn.options?.every((o) => !o.selectToOptIn) == true;
  const { theme } = useTenant();
  const companyName = theme?.p2g.siteName || '';
  const [ref, inView] = useP2GInView({ triggerOnce: true });

  useEffect(() => {
    if (!optIn.loading && inView) {
      addCompletedAction('COMMS');
    }
  }, [optIn.loading, inView, addCompletedAction]);

  if (optIn.loading) {
    return null;
  }

  if (optIn.options.length === 0) {
    return optIn.contentKey
      ? wrapper(
          <>
            <div ref={ref} />
            <P2GFade in>
              <P2GText variant="body2">
                <Trans i18nKey={`OPTIN_MESSAGE_NO_OPTIONS_${optIn.contentKey || ''}`} t={t}>
                  {GetDefaultOptInMessage(optIn.contentKey, companyName)}
                </Trans>
              </P2GText>
            </P2GFade>
          </>
        )
      : null;
  }

  return wrapper(
    <>
      <div ref={ref} />
      <P2GFade in>
        <P2GGrid container spacing={1} dataTestId="marketing-opt-in">
          <P2GGrid xs={12} item>
            <P2GText dataTestId="marketing-opt-in-title">
              {t(selectToOptOut ? 'OPTIN_MESSAGE_SELECT_TO_OPT_OUT_TOP' : 'OPTIN_MESSAGE_SELECT_TO_OPT_IN_TOP', {
                defaultValue: selectToOptOut
                  ? `We will keep you updated with valuable information, such as exclusive offers, products and services. ` +
                    `If you prefer not to receive these updates, please check the relevant boxes below:`
                  : `We will keep you updated with valuable information, such as exclusive offers, products and services. ` +
                    `To be included in our mailing list, just tick the boxes below to confirm:`
              })}
            </P2GText>
          </P2GGrid>
          {optIn.options.map((option) => (
            <P2GGrid xs={6} item key={option.communicationType}>
              <P2GCheckBox
                dataTestId={`marketing-opt-in-option-${option.communicationType}`}
                labelPlacement="end"
                variant="primary"
                size="small"
                error={false}
                title={option.title}
                inputProps={{
                  checked: option.selected,
                  onChange: () => optIn.update(option.communicationType)
                }}
              >
                {t(`OPTIN_OPTION_${option.communicationType}`.toUpperCase(), { defaultValue: option.title })}
              </P2GCheckBox>
            </P2GGrid>
          ))}
          <P2GGrid xs={12} item>
            <P2GText variant="body2" dataTestId="marketing-opt-in-subtitle">
              <Trans
                i18nKey={
                  selectToOptOut ? 'OPTIN_MESSAGE_SELECT_TO_OPT_OUT_BOTTOM' : 'OPTIN_MESSAGE_SELECT_TO_OPT_IN_BOTTOM'
                }
                t={t}
                defaults="You can change your mind at any time by opting out in your My Account area or clicking the unsubscribe link. 'We' means {{companyName}}, we'll never sell or share your information with anyone else. <0>Click here</0> to view our privacy policy."
                values={{ companyName: companyName }}
                components={[<P2GLink href="/privacy-policy" target="_blank" />]}
              />
            </P2GText>
          </P2GGrid>
        </P2GGrid>
      </P2GFade>
    </>
  );
};

export default OptIn;
